import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { 
  Mail, TrendingUp, DollarSign, Calendar, Filter, 
  Upload, Image as ImageIcon, CheckCircle, XCircle,
  BarChart3, PieChart, Activity
} from 'lucide-react';
import { toast } from 'sonner';

export default function SuperAdminSettingsPage() {
  // Email Stats State
  const [emailStats, setEmailStats] = useState(null);
  const [restaurantStats, setRestaurantStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('month'); // today, week, month, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Logo State
  const [globalLogo, setGlobalLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Load email stats
  const loadEmailStats = async (start, end) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('superadmin_token');
      
      if (!token) {
        toast.error('Vous devez être connecté');
        return;
      }
      
      // Build query params
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      
      // Headers with Authorization
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      // Get global stats
      const globalRes = await fetch(`/api/superadmin/email-stats/global?${params.toString()}`, { headers });
      if (!globalRes.ok) {
        throw new Error(`HTTP ${globalRes.status}`);
      }
      const globalData = await globalRes.json();
      setEmailStats(globalData);
      
      // Get restaurant breakdown
      const restaurantsRes = await fetch(`/api/superadmin/email-stats/by-restaurant?${params.toString()}`, { headers });
      if (!restaurantsRes.ok) {
        throw new Error(`HTTP ${restaurantsRes.status}`);
      }
      const restaurantsData = await restaurantsRes.json();
      setRestaurantStats(restaurantsData.restaurants || []);
      
    } catch (error) {
      console.error('Error loading email stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  // Calculate date range based on filter
  const getDateRange = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    switch(dateFilter) {
      case 'today':
        return { start: today, end: today };
      case 'week':
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { start: weekAgo.toISOString().split('T')[0], end: today };
      case 'month':
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return { start: monthAgo.toISOString().split('T')[0], end: today };
      case 'custom':
        return { start: customStartDate, end: customEndDate };
      default:
        return { start: null, end: null };
    }
  };

  // Load stats on mount and when filter changes
  useEffect(() => {
    const { start, end } = getDateRange();
    loadEmailStats(start, end);
  }, [dateFilter]);

  // Handle custom date filter
  const applyCustomFilter = () => {
    if (!customStartDate || !customEndDate) {
      toast.error('Veuillez sélectionner une période');
      return;
    }
    loadEmailStats(customStartDate, customEndDate);
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    try {
      setUploadingLogo(true);
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        setLogoPreview(base64);
        
        // Upload to backend
        const token = localStorage.getItem('superadmin_token');
        const response = await fetch(`/api/superadmin/settings/logo`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ logo_base64: base64 })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        toast.success('Logo global mis à jour');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading && !emailStats) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Paramètres Globaux</h1>
        <p className="text-gray-500 mt-1">Gestion des emails, logo et configuration système</p>
      </div>

      {/* Date Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtres de Période
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {/* Predefined filters */}
            <Button 
              variant={dateFilter === 'today' ? 'default' : 'outline'}
              onClick={() => setDateFilter('today')}
              size="sm"
            >
              Aujourd'hui
            </Button>
            <Button 
              variant={dateFilter === 'week' ? 'default' : 'outline'}
              onClick={() => setDateFilter('week')}
              size="sm"
            >
              7 derniers jours
            </Button>
            <Button 
              variant={dateFilter === 'month' ? 'default' : 'outline'}
              onClick={() => setDateFilter('month')}
              size="sm"
            >
              30 derniers jours
            </Button>
            <Button 
              variant={dateFilter === 'custom' ? 'default' : 'outline'}
              onClick={() => setDateFilter('custom')}
              size="sm"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Personnalisé
            </Button>
          </div>

          {/* Custom date range */}
          {dateFilter === 'custom' && (
            <div className="mt-4 flex gap-3 items-end">
              <div className="flex-1">
                <Label>Date de début</Label>
                <Input 
                  type="date" 
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label>Date de fin</Label>
                <Input 
                  type="date" 
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
              <Button onClick={applyCustomFilter}>
                Appliquer
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Stats Dashboard */}
      {emailStats && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Emails</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      {emailStats.summary.total_emails.toLocaleString()}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-green-600 font-medium">
                    {emailStats.summary.success_rate}% succès
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Envoyés</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">
                      {emailStats.summary.successful.toLocaleString()}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Échecs</p>
                    <p className="text-3xl font-bold text-red-600 mt-2">
                      {emailStats.summary.failed.toLocaleString()}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Coût Estimé</p>
                    <p className="text-3xl font-bold text-purple-600 mt-2">
                      {emailStats.cost.estimated_total_cost}€
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-4">
                  {emailStats.cost.cost_per_email}€ par email
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Type Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                Répartition par Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(emailStats.by_type).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        type === 'order' ? 'bg-blue-500' : 'bg-green-500'
                      }`}></div>
                      <span className="font-medium capitalize">{type}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-2xl font-bold">{count.toLocaleString()}</span>
                      <span className="text-sm text-gray-500">
                        {((count / emailStats.summary.total_emails) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Restaurant Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Par Restaurant
              </CardTitle>
              <CardDescription>
                Top {restaurantStats.slice(0, 10).length} restaurants par volume d'emails
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {restaurantStats.slice(0, 10).map((rest) => (
                  <div key={rest.restaurant_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{rest.restaurant_name}</p>
                      <p className="text-xs text-gray-500">
                        {rest.successful_emails} envoyés • {rest.failed_emails} échecs
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold">{rest.total_emails}</p>
                      <p className="text-xs text-purple-600">{rest.estimated_cost}€</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Logo Global Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Logo Global ORDER LAB
          </CardTitle>
          <CardDescription>
            Ce logo sera utilisé par défaut pour les restaurants n'ayant pas uploadé leur propre logo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6">
            {/* Logo Preview */}
            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-gray-50">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <ImageIcon className="w-12 h-12 text-gray-400" />
              )}
            </div>

            {/* Upload Button */}
            <div>
              <Label htmlFor="logo-upload" className="cursor-pointer">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                  <Upload className="w-4 h-4" />
                  {uploadingLogo ? 'Upload en cours...' : 'Uploader un logo'}
                </div>
              </Label>
              <Input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                disabled={uploadingLogo}
              />
              <p className="text-xs text-gray-500 mt-2">
                Format : PNG, JPG, SVG • Taille recommandée : 512x512px
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>ℹ️ Impact du logo global :</strong><br />
              Ce logo sera utilisé dans la sidebar, les PDFs, le favicon et comme logo PWA pour tous les restaurants qui n'ont pas uploadé leur propre logo dans Paramètres &gt; Logo.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
